/**
 * Firm logo — the one piece of a firm's identity that follows its members
 * into the app header and onto the top of every report.
 *
 * Where it lives: the public `firm-logos` bucket (20260623000000), one folder
 * per firm, writable only by that firm's admins under storage RLS; the
 * current file's address is `firms.logo_url`, null when there is no logo.
 * Every upload takes a fresh timestamped name rather than overwriting,
 * because public objects sit behind a CDN cache — an overwritten `logo.png`
 * could keep serving the old image for an hour after "Replace".
 *
 * Enterprise only: individual accounts have no firm and therefore no logo.
 */
import { supabase } from './supabase';

export const FIRM_LOGO_BUCKET = 'firm-logos';

/** Mirrors the bucket's file_size_limit; checked here for a readable message. */
export const FIRM_LOGO_MAX_BYTES = 2 * 1024 * 1024;

/** Mirrors the bucket's allowed_mime_types. */
export const FIRM_LOGO_ACCEPTED_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
];

/**
 * Every upload is rasterised to fit this box. Wide enough to print sharply at
 * the report's letterhead size (2.6in at 300dpi is 780px), small enough that
 * the copy embedded in a report stays a few tens of kilobytes.
 */
export const FIRM_LOGO_MAX_WIDTH = 1200;
export const FIRM_LOGO_MAX_HEIGHT = 400;

/** A human-readable reason the file cannot be used, or null when it can. */
export function validateFirmLogoFile(file: { type: string; size: number }): string | null {
  if (!FIRM_LOGO_ACCEPTED_TYPES.includes(file.type)) {
    return 'Please choose a PNG, JPG, WebP, or SVG image.';
  }
  if (file.size === 0) return 'That file is empty.';
  if (file.size > FIRM_LOGO_MAX_BYTES) return 'Logo files must be 2 MB or smaller.';
  return null;
}

/**
 * Object path inside the bucket: `<firm id>/logo-<stamp>.png`. The first
 * folder segment is what the storage policies hand to is_firm_admin().
 */
export function firmLogoObjectPath(firmId: string, stamp: number): string {
  return `${firmId}/logo-${stamp}.png`;
}

/**
 * The bucket path behind a public URL this module issued, or null for
 * anything else. Cleanup deletes only by a path derived this way — never by
 * a string it did not build itself.
 */
export function firmLogoObjectPathFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const marker = `/storage/v1/object/public/${FIRM_LOGO_BUCKET}/`;
  const at = url.indexOf(marker);
  if (at < 0) return null;
  const rest = url.slice(at + marker.length).split(/[?#]/)[0];
  if (!rest) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    return null;
  }
}

/** Fits an image inside the letterhead box, never scaling up. */
export function fitLogoDimensions(width: number, height: number): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 1, height: 1 };
  const scale = Math.min(1, FIRM_LOGO_MAX_WIDTH / width, FIRM_LOGO_MAX_HEIGHT / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

/**
 * Normalises any accepted upload into a PNG that fits the letterhead box.
 * Rasterising in the browser gives the stored file a predictable size and
 * format whatever was chosen — a photograph-grade JPEG, or an SVG that the
 * print path and headless Chromium would otherwise have to parse.
 */
export async function normalizeFirmLogo(file: File): Promise<Blob> {
  const img = await loadImage(file);
  // An SVG without intrinsic dimensions reports 0×0; draw it at a sensible size.
  const { width, height } = fitLogoDimensions(img.naturalWidth || 600, img.naturalHeight || 200);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Your browser could not process the image.');
  ctx.drawImage(img, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('Your browser could not process the image.'))),
      'image/png'
    );
  });
}

async function removeFirmLogoObject(url: string | null | undefined): Promise<void> {
  const path = firmLogoObjectPathFromUrl(url);
  if (!path) return;
  // Best effort: the firm record is already correct, and a stranded file is
  // a few kilobytes nobody can find. Not worth failing the user's action over.
  const { error } = await supabase.storage.from(FIRM_LOGO_BUCKET).remove([path]);
  if (error) console.warn('Firm logo cleanup failed:', error.message);
}

async function saveFirmLogoUrl(firmId: string, logoUrl: string | null): Promise<void> {
  const { data, error } = await supabase
    .from('firms')
    .update({ logo_url: logoUrl, updated_at: new Date().toISOString() })
    .eq('id', firmId)
    .select('id');
  if (error) throw new Error(error.message);
  // RLS filters silently: zero rows back means the caller is not this firm's admin.
  if (!data || data.length === 0) throw new Error('Only a firm admin can change the logo.');
}

/**
 * Uploads a new logo for the firm and records it. Returns the public URL now
 * stored on the firm. The previous file is deleted only after the new one is
 * recorded, so a failure part-way leaves the firm on its old logo, not none.
 */
export async function uploadFirmLogo(firmId: string, file: File, previousUrl: string | null): Promise<string> {
  const problem = validateFirmLogoFile(file);
  if (problem) throw new Error(problem);

  const png = await normalizeFirmLogo(file);
  const path = firmLogoObjectPath(firmId, Date.now());
  const bucket = supabase.storage.from(FIRM_LOGO_BUCKET);

  const { error: uploadError } = await bucket.upload(path, png, {
    contentType: 'image/png',
    cacheControl: '3600',
    upsert: false,
  });
  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  const { data: { publicUrl } } = bucket.getPublicUrl(path);
  try {
    await saveFirmLogoUrl(firmId, publicUrl);
  } catch (err) {
    // Don't strand a file the firm can never see.
    await bucket.remove([path]);
    throw new Error(`Could not save the logo: ${err instanceof Error ? err.message : 'unknown error'}`);
  }

  if (previousUrl !== publicUrl) await removeFirmLogoObject(previousUrl);
  return publicUrl;
}

/** Clears the firm's logo and deletes the file. */
export async function removeFirmLogo(firmId: string, currentUrl: string | null): Promise<void> {
  try {
    await saveFirmLogoUrl(firmId, null);
  } catch (err) {
    throw new Error(`Could not remove the logo: ${err instanceof Error ? err.message : 'unknown error'}`);
  }
  await removeFirmLogoObject(currentUrl);
}

/**
 * The logo as something a report can carry on its own. The printed report
 * and the server-rendered PDF both open the HTML in a browser that may not
 * be able to reach storage at that moment — generate-pdf runs in headless
 * Chromium on Netlify — so the image is embedded as a data URL. Falls back
 * to the plain URL rather than dropping the logo if the fetch fails.
 */
export async function firmLogoForReport(url: string | null | undefined): Promise<string | null> {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}
